import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatOdds } from '@/lib/format-odds';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { cn } from '@/lib/utils';
import type { TrackedSignalBookOdds } from '@tennis-scanner/shared';
import { Lock } from 'lucide-react';

// Per-book odds — DENSE single-row strip used by both OpportunityCard and
// TrackedPickCard. Aggressively compressed: gap-0.5, no padding-y on chips,
// text-[10px]. Books are pre-sorted by odds desc; allowed first.
//
// Visual coding:
//   - Allowed + meets target  → green (operator can act)
//   - Allowed + below target  → red (book exists but not paying yet)
//   - Reference (any odds)    → muted gray + lock icon

interface Props {
  perBook: TrackedSignalBookOdds[];
  targetOdds: number;
  emptyHint?: string;
}

export function PerBookStrip({ perBook, targetOdds, emptyHint }: Props) {
  if (perBook.length === 0) {
    return (
      <p className="mt-1 text-[10px] italic leading-tight text-muted-foreground">
        {emptyHint ??
          'Sin cuotas todavía — el connector publica el primer tick a los ~30s del partido.'}
      </p>
    );
  }
  const sorted = [...perBook].sort((a, b) => {
    if (a.isAllowed !== b.isAllowed) return a.isAllowed ? -1 : 1;
    return b.odds - a.odds;
  });
  return (
    <div className="mt-1 flex flex-wrap gap-0.5">
      {sorted.map((b) => (
        <BookChip key={b.book} book={b} targetOdds={targetOdds} />
      ))}
    </div>
  );
}

// Backward-compat alias so existing imports (TrackedPickCard) keep compiling.
export const PerBookGrid = PerBookStrip;

function BookChip({ book, targetOdds }: { book: TrackedSignalBookOdds; targetOdds: number }) {
  const meets = book.odds >= targetOdds;
  const cls = book.isAllowed
    ? meets
      ? 'border-positive/40 bg-positive/10 text-positive'
      : 'border-destructive/40 bg-destructive/10 text-destructive'
    : 'border-border bg-muted/30 text-muted-foreground';

  const edgePct = ((book.odds / targetOdds - 1) * 100).toFixed(1);
  const edgeSign = Number(edgePct) >= 0 ? '+' : '';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-baseline gap-1 rounded-sm border px-1 py-0 text-[10px] leading-tight',
            cls,
          )}
        >
          {!book.isAllowed && <Lock className="size-2.5 self-center opacity-60" />}
          <span className="font-medium">{book.book}</span>
          <span className="font-mono font-semibold">{formatOdds(book.odds)}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs space-y-0.5 text-[11px]">
        <div className="font-medium text-foreground">
          {book.book} · {formatOdds(book.odds)}
        </div>
        <div className="font-mono text-muted-foreground">
          edge vs target: {edgeSign}
          {edgePct}% (target {formatOdds(targetOdds)})
        </div>
        <div className="font-mono text-muted-foreground">
          observado {formatRelativeTime(book.observedAt)}
        </div>
        <div className="text-muted-foreground">
          {book.isAllowed ? 'casa operable · genera alerta' : 'referencia · no genera alerta'}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
